// Semantic search for document-mode snippets
// Helps beginners discover Tab-triggered snippets by typing natural language

import { DOCUMENT_SNIPPETS, type DocumentSnippet } from "./document-snippets";
import { createLatexSnippets, type SnippetDefinition } from "./snippet-extension";

// Merge document snippets and structural snippet triggers into one searchable list
export interface DocSnippetSuggestion {
    trigger: string;
    name: string;
    body: string;  // LaTeX body or description
    score: number;
    matchedOn: string;
}

// Triggers that only make sense inside math mode — excluded from doc-mode search
const MATH_ONLY_TRIGGERS = new Set([
    // Fractions
    "ff", "df", "pder", "dv",
    // Sums / products / integrals
    "sum", "prod", "lim", "int", "iint", "oint",
    // Roots
    "sq", "nrt",
    // Superscript / subscript
    "td", "rd", "cb", "sr",
    // Delimiters
    "lr(", "lr[", "lr{", "lr|", "lr<", "norm",
    // Math fonts
    "mcal", "mbb", "mbf", "mrm",
    // Accents
    "hat", "bar", "vec", "dot", "ddot", "tld",
    // Symbols
    "inf", "iff", "=>>", "=<<", "nabl", "xx", "ox", "opl", "cdot", "**", "pm",
    // Greek letters (semicolon triggers)
    ";a", ";b", ";g", ";G", ";d", ";D", ";e", ";ve", ";z", ";h",
    ";q", ";Q", ";vq", ";k", ";l", ";L", ";m", ";n", ";x", ";X",
    ";p", ";P", ";r", ";s", ";S", ";vs", ";t", ";u", ";U",
    ";f", ";vf", ";F", ";c", ";y", ";Y", ";w", ";W",
]);

// Additional keywords for doc snippets beyond their name/trigger
const docKeywords: Record<string, string[]> = {
    // Structure
    "scn": ["section", "heading", "header", "h1"],
    "sbn": ["subsection", "subheading", "h2"],
    "ssn": ["subsubsection", "h3"],
    "sec": ["section", "heading", "header", "h1"],
    "ssec": ["subsection", "subheading", "h2"],
    "sssec": ["subsubsection", "h3"],
    // Environments
    "oo": ["theorem", "thm"],
    "ll": ["lemma", "lem"],
    "ps": ["proposition", "prop"],
    "cc": ["corollary", "cor"],
    "dd": ["definition", "defn", "def"],
    "re": ["remark", "rem", "note"],
    "ee": ["example", "eg"],
    "pf": ["proof", "qed"],
    "ex": ["exercise", "problem"],
    "en": ["environment", "begin", "end"],
    "thm": ["theorem"],
    "lem": ["lemma"],
    "prop": ["proposition"],
    "cor": ["corollary"],
    "defn": ["definition"],
    // Math delimiters (these start math from doc mode, so keep them)
    "dm": ["display math", "equation", "displaymath", "\\["],
    "mm": ["inline math", "dollar", "math mode"],
    "mk": ["inline math", "math mode"],
    "eq": ["equation", "numbered equation"],
    "ali": ["align", "alignment", "aligned"],
    // Lists
    "tz": ["itemize", "bullet", "unordered list", "bullets"],
    "enn": ["enumerate", "numbered list", "ordered list", "numbers"],
    "enum": ["enumerate", "numbered list", "ordered list"],
    "item": ["itemize", "bullet list", "unordered list"],
    // Code
    "vb": ["verbatim", "code", "monospace", "preformatted"],
    "code": ["code block", "verbatim", "listing"],
    "tt": ["teletype", "monospace", "inline code", "texttt"],
    // Figure & table
    "fig": ["figure", "image", "picture", "includegraphics"],
    "tbl": ["table", "tabular", "grid"],
    // Metadata
    "tl": ["title", "document title"],
    "aut": ["author", "authors"],
    "abs": ["abstract", "summary"],
};

// Build a unified set of searchable items from DOCUMENT_SNIPPETS
// plus the programmatic SnippetDefinition triggers that aren't in DOCUMENT_SNIPPETS
let _cachedItems: { trigger: string; name: string; body: string }[] | null = null;

function getSearchItems(): { trigger: string; name: string; body: string }[] {
    if (_cachedItems) return _cachedItems;

    const seen = new Set<string>();
    const items: { trigger: string; name: string; body: string }[] = [];

    // Add DOCUMENT_SNIPPETS first (excluding math-only triggers)
    for (const s of DOCUMENT_SNIPPETS) {
        if (MATH_ONLY_TRIGGERS.has(s.trigger)) continue;
        seen.add(s.trigger);
        items.push({ trigger: s.trigger, name: s.name, body: s.body });
    }

    // Add programmatic snippets (from createLatexSnippets) that aren't in DOCUMENT_SNIPPETS
    const programmatic = createLatexSnippets();
    for (const s of programmatic) {
        if (!s.trigger || seen.has(s.trigger) || MATH_ONLY_TRIGGERS.has(s.trigger)) continue;
        seen.add(s.trigger);
        // For programmatic snippets, name isn't stored — use keywords or trigger
        const kw = docKeywords[s.trigger];
        const name = kw ? kw[0] : s.trigger;
        items.push({ trigger: s.trigger, name, body: "" });
    }

    _cachedItems = items;
    return items;
}

/**
 * Search document snippets by fuzzy matching.
 * Returns suggestions sorted by relevance.
 */
export function searchDocSnippets(query: string, limit: number = 6): DocSnippetSuggestion[] {
    if (!query || query.length < 2) return [];

    const normalizedQuery = query.toLowerCase().trim();
    const suggestions: DocSnippetSuggestion[] = [];

    for (const item of getSearchItems()) {
        let bestScore = 0;
        let matchedOn = "";

        // Check name
        const nameMatch = fuzzyMatch(normalizedQuery, item.name.toLowerCase());
        if (nameMatch > bestScore) {
            bestScore = nameMatch;
            matchedOn = item.name;
        }

        // Check trigger
        const triggerMatch = fuzzyMatch(normalizedQuery, item.trigger.toLowerCase());
        if (triggerMatch > bestScore) {
            bestScore = triggerMatch;
            matchedOn = item.trigger;
        }

        // Check keywords
        const keywords = docKeywords[item.trigger];
        if (keywords) {
            for (const kw of keywords) {
                const kwMatch = fuzzyMatch(normalizedQuery, kw.toLowerCase());
                if (kwMatch > bestScore) {
                    bestScore = kwMatch;
                    matchedOn = kw;
                }
            }
        }

        if (bestScore > 0.3) {
            suggestions.push({
                trigger: item.trigger,
                name: item.name,
                body: item.body,
                score: bestScore,
                matchedOn,
            });
        }
    }

    suggestions.sort((a, b) => b.score - a.score);
    return suggestions.slice(0, limit);
}

/** Simple fuzzy match — returns score between 0 and 1 */
function fuzzyMatch(query: string, target: string): number {
    if (query === target) return 1;
    if (target.startsWith(query)) return 0.9;
    if (target.includes(query)) return 0.7;

    let queryIdx = 0;
    let consecutiveBonus = 0;
    let lastMatchIdx = -2;

    for (let i = 0; i < target.length && queryIdx < query.length; i++) {
        if (target[i] === query[queryIdx]) {
            if (i === lastMatchIdx + 1) consecutiveBonus += 0.1;
            lastMatchIdx = i;
            queryIdx++;
        }
    }

    if (queryIdx === query.length) {
        const baseScore = query.length / target.length;
        return Math.min(0.6 + consecutiveBonus, 0.85) * baseScore + 0.15;
    }

    return 0;
}
